import { IIdentityContext } from '@almamedia-legal-insights/types';
import { Typography, Box, Stack, Divider, Link, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { JURISTILIITTO_CONNECTION_ID } from '../../src/components/Auth/AuthProvider';
import { FeatureFlag } from '../../src/common/components/FeatureFlag';

export default function AccountMenuContent({
  identityContext,
  closeMenu,
}: Readonly<{
  identityContext: IIdentityContext;
  closeMenu: () => void;
}>) {
  const { t } = useTranslation();
  const { authInfo, user, loading, actions } = identityContext;
  const isIpAuthenticated = Boolean(authInfo?.ipAuth);
  const organization = authInfo?.organization;
  const isAuthenticatedUser = user !== undefined;
  const showLoginOptions = !isAuthenticatedUser && !isIpAuthenticated;

  const handleLoginRegular = () => {
    actions?.login();
    closeMenu();
  };

  const handleLoginJuristiliitto = () => {
    if (actions?.login) {
      actions.login(JURISTILIITTO_CONNECTION_ID);
      closeMenu();
    } else {
      console.info('Actions not yet loaded');
    }
  };

  return (
    <Stack
      direction='column'
      sx={{
        width: { xs: '100%', sm: 245 },
        maxWidth: '100%',
        paddingX: 2,
        paddingY: 2,
        boxSizing: 'border-box',
      }}
      spacing={1.5}
    >
      {isIpAuthenticated && !isAuthenticatedUser && (
        <Box>
          <Typography
            variant='body2'
            sx={{ marginBottom: 0.5, color: 'text.secondary' }}
          >
            {t('accountMenu.ipAccess')}
          </Typography>
          {!loading && <Typography>{organization}</Typography>}
          <Divider sx={{ marginY: 1.5 }} />
        </Box>
      )}

      {isAuthenticatedUser && (
        <Box>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t('accountMenu.loggedInAs')}
          </Typography>
          <Typography
            variant='subtitle2'
            sx={{ wordBreak: 'break-all', marginBottom: 1.5 }}
          >
            {user?.email}
          </Typography>
          <Link
            component='button'
            onClick={() => {
              actions?.logout();
              closeMenu();
            }}
            variant='body2'
            underline='hover'
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              textAlign: 'left',
              p: 0,
              border: 'none',
              background: 'none',
            }}
          >
            {t('accountMenu.logOut')}
          </Link>
        </Box>
      )}

      {showLoginOptions && (
        <>
          <Button variant='contained' fullWidth onClick={handleLoginRegular}>
            {t('accountMenu.loginRegular')}
          </Button>
          {authInfo && (
            <FeatureFlag flagName='juristiliitto-login' authInfo={authInfo}>
              <Button
                variant='outlined'
                fullWidth
                onClick={handleLoginJuristiliitto}
              >
                {t('accountMenu.loginJuristiliitto')}
              </Button>
            </FeatureFlag>
          )}
        </>
      )}

      {isIpAuthenticated && !isAuthenticatedUser && (
        <>
          <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
            {t('accountMenu.ipAuthLoginPrompt')}
          </Typography>
          <Button
            variant='contained'
            fullWidth
            onClick={handleLoginRegular}
            sx={{ mt: 1 }}
          >
            {t('accountMenu.loginRegular')}
          </Button>
          {authInfo && (
            <FeatureFlag flagName='juristiliitto-login' authInfo={authInfo}>
              <Button
                variant='outlined'
                fullWidth
                onClick={handleLoginJuristiliitto}
                sx={{ mt: 1 }}
              >
                {t('accountMenu.loginJuristiliitto')}
              </Button>
            </FeatureFlag>
          )}
        </>
      )}
    </Stack>
  );
}
